import { observable, computed } from 'mobx';
import { autobind } from 'core-decorators';
import { loadActivityContext } from '@src/services/activity-session';

const requestOptionsByPromise = new WeakMap();

function canReuseRequest(existingOptions = {}, requestedOptions = {}) {
  const existingKeys = Object.keys(existingOptions);
  const requestedKeys = Object.keys(requestedOptions);

  // A contextless caller only consumes the stable ViewModel, so an enriched invite request satisfies it.
  return (
    requestedKeys.length === 0 ||
    (existingKeys.length === requestedKeys.length &&
      existingKeys.every(key => Object.prototype.hasOwnProperty.call(requestedOptions, key) && existingOptions[key] === requestedOptions[key]))
  );
}

function getCurrentAccount() {
  if (typeof window === 'undefined' || !window.XPASS || typeof window.XPASS.u_account !== 'string') {
    return null;
  }

  return window.XPASS.u_account || null;
}

@autobind
export default class ActivitySessionStore {
  // 共享 Store 只保存跨页需要的稳定 Context 字段（account 为服务端脱敏通行位，右上角登录态数据源；
  // personalShareCode 为 get_config 返回的当前用户个人分享码，组队转发客态链接的归因参数——
  // 与入口邀请参数 shareCode 语义不同，命名区分以守住"邀请上下文不入共享 ViewModel"的边界契约）。
  @observable activityStage = null;
  @observable playerType = null;
  @observable account = null;
  @observable personalShareCode = null;
  // get_config 的回顾完成事实（0 未完成 / 1 已完成）；null=尚未获得事实（未拉取/失败），
  // 底栏组队域门控按"未完成回顾"保守处理（2026-09-20 需求）
  @observable reviewStatus = null;
  @observable loadStatus = 'idle';
  @observable.ref loadError = null;
  @observable.ref initializingPromise = null;
  @observable contextOwnerAccount = null;

  /**
   * 在当前稳定账号下初始化活动上下文，同账号已就绪时返回缓存。
   * 每次读缓存前都会对比 `XPASS.u_account`，防止页面切换后串用上一账号事实。
   * 不兼容的飞行中请求会被取代，避免首页无参请求吞掉后到的邀请归因。
   *
   * @param {Object} options 显式邀请上下文；仅透传给本次请求，不写入共享 Store。
   * @returns {Promise<{activityStage:string, playerType:string}|undefined>} 当前账号上下文；过期请求安全返回 undefined。
   */
  ensureInitialized(options = {}) {
    const currentAccount = getCurrentAccount();

    if (this.contextOwnerAccount !== currentAccount) {
      this.invalidate();
    }

    if (this.initializingPromise && canReuseRequest(requestOptionsByPromise.get(this.initializingPromise), options)) {
      return this.initializingPromise;
    }

    // Explicit entry context must reach get_config even when the homepage has already populated the account cache.
    if (options && Object.keys(options).length > 0) {
      return this.load(currentAccount, options);
    }

    if (this.loadStatus === 'ready') {
      return Promise.resolve(this.getContext());
    }

    return this.load(currentAccount, options);
  }

  /**
   * 强制刷新当前账号的活动上下文；满足当前调用数据需求的飞行中请求可复用。
   * 后发的不兼容请求取代当前锁，旧响应仍由 Promise 引用校验阻止回写。
   *
   * @param {Object} options 显式邀请上下文；仅透传给本次请求，不写入共享 Store。
   * @returns {Promise<{activityStage:string, playerType:string}|undefined>} 刷新结果；过期请求安全返回 undefined。
   */
  refresh(options = {}) {
    const currentAccount = getCurrentAccount();

    if (this.contextOwnerAccount !== currentAccount) {
      this.invalidate();
    }

    if (this.initializingPromise && canReuseRequest(requestOptionsByPromise.get(this.initializingPromise), options)) {
      return this.initializingPromise;
    }

    return this.load(currentAccount, options);
  }

  /**
   * 使当前用户级活动事实失效，不处理任何页面弹窗、导航或 Team 状态。
   *
   * @returns {void}
   */
  invalidate() {
    this.activityStage = null;
    this.playerType = null;
    this.account = null;
    this.personalShareCode = null;
    this.reviewStatus = null;
    this.loadStatus = 'idle';
    this.loadError = null;
    this.initializingPromise = null;
    this.contextOwnerAccount = null;
  }

  /**
   * 重置领域会话，语义与账号失效相同。
   *
   * @returns {void}
   */
  reset() {
    this.invalidate();
  }

  /**
   * 当前账号已完成回顾：回顾页 finish_review 成功后即时同步共享会话事实，
   * 避免后续页面继续消费 get_config 的旧缓存而误判 reviewCleared=false。
   *
   * @returns {boolean} 是否命中当前 owner 并完成同步。
   */
  markReviewCompleted() {
    if (this.contextOwnerAccount !== getCurrentAccount()) {
      return false;
    }

    this.reviewStatus = 1;
    return true;
  }

  /**
   * 构建页面 Store 可复用的最小缓存快照。
   *
   * @returns {{activityStage:string|null, playerType:string|null, reviewStatus:number|null}} 不包含页面编排字段的活动上下文。
   */
  getContext() {
    return {
      activityStage: this.activityStage,
      playerType: this.playerType,
      account: this.account,
      shareCode: this.personalShareCode,
      reviewStatus: this.reviewStatus
    };
  }

  /**
   * 回顾义务是否已清：底栏组队域门控的共享判断口径（2026-09-20 用户需求，
   * 由"是否组队"改为"是否完成回顾"）。新玩家（playerType 'new'）无回顾义务直接通过；
   * 老玩家以 get_config.review_status === 1（已完成回顾）为准；事实未知（null）视为未清，
   * 由调用方保守回落活动首页交由身份分流承接。
   */
  @computed
  get reviewCleared() {
    return this.playerType === 'new' || this.reviewStatus === 1;
  }

  /**
   * 启动一次可共享的 Provider 请求，并仅允许当前 owner 的锁回写状态。
   * 请求选项只关联到 Promise 的弱引用，用于并发兼容判断，不成为 root Store 状态。
   *
   * @param {string|null} ownerAccount 请求发起时从 `XPASS.u_account` 取得的稳定账号。
   * @param {Object} options 原样传给 loadActivityContext 的本次请求选项。
   * @returns {Promise<{activityStage:string, playerType:string}|undefined>} 当前 owner 的上下文；过期请求安全返回 undefined。
   */
  load(ownerAccount, options = {}) {
    this.loadStatus = 'loading';
    this.loadError = null;
    this.contextOwnerAccount = ownerAccount;

    const request = loadActivityContext(options).then(
      context => {
        // A 的旧请求不得清理 B 已替换的新锁，也不得把旧上下文交给调用方继续分流。
        if (this.initializingPromise !== request) {
          return undefined;
        }

        // 账号变更校验只对发起时已知 owner 生效：发起时为 null 表示 XPASS 尚未写入 u_account
        //（SDK 确认登录态滞后于页面挂载），请求飞行期间账号补全不是变更——响应以 cookie 登录态
        // 返回，本就属于当前账号；误判作废会让消费页把 undefined 当异常报错（2026-09-24 修复）。
        const committedOwnerAccount = ownerAccount !== null ? ownerAccount : getCurrentAccount();
        if (ownerAccount !== null && committedOwnerAccount !== ownerAccount) {
          this.invalidate();
          return undefined;
        }

        this.activityStage = context.activityStage;
        this.playerType = context.playerType;
        this.account = context.account;
        this.personalShareCode = typeof context.shareCode === 'string' ? context.shareCode : null;
        this.reviewStatus = typeof context.reviewStatus === 'number' ? context.reviewStatus : null;
        this.loadStatus = 'ready';
        this.loadError = null;
        // 发起时 owner 未知时登记响应时账号，避免残留 null 使分享 reaction/队伍事实自动补齐持续跳过
        this.contextOwnerAccount = committedOwnerAccount;
        this.initializingPromise = null;
        return context;
      },
      error => {
        // 被新 owner 替换的请求只能向原调用方 reject，不能改变新请求的状态或锁。
        if (this.initializingPromise !== request) {
          throw error;
        }

        // 与成功分支同口径：发起时 owner 未知的请求，错误按当前状态正常提交，不作废
        if (ownerAccount !== null && getCurrentAccount() !== ownerAccount) {
          this.invalidate();
          throw error;
        }

        this.activityStage = null;
        this.playerType = null;
        this.reviewStatus = null;
        this.loadStatus = 'error';
        this.loadError = error;
        this.initializingPromise = null;
        throw error;
      }
    );

    requestOptionsByPromise.set(request, options);
    this.initializingPromise = request;
    return request;
  }
}
